import { useEffect, useRef, useState, useCallback } from "react";

import { apiBaseUrls } from "@platform/constants/shared-constants";

// ── Types ─────────────────────────────────────────────────────────────────────

/* eslint-disable unused-imports/no-unused-vars */
export interface SseMessage<T = unknown> {
  /** Event type (e.g. "status_changed", "deployment", "notification.new") */
  type: string;
  /** Event payload */
  data: T;
}

/**
 * Named SSE event channels emitted by the API's NestJS @Sse controllers
 * (src/api/src/modules/deployment-events/sse.controller.ts).
 *
 * NestJS MessageEvent serialization puts the `type` field in the SSE
 * `event:` line, so the browser only fires a listener registered for that
 * exact name — the generic `message` listener never fires for them. This
 * registry is the client-side mirror of the server contract; see the
 * `sse_transport` section in contracts/pipeline-architecture.yaml.
 *
 * Control events (`connected`, `heartbeat`, `error`) are stream state, not
 * data, and are handled inside useSse rather than forwarded to consumers.
 */
export const SSE_EVENT_NAMES = [
  // sse/stack/:alias
  "deployment",
  "deployment.log",
  "status_changed",
  "gitops_status",
  "pipeline_run",
  // sse/project/:projectAlias
  "resource.status_changed",
  "application_created",
  // sse/notifications
  "notification.new",
  // sse/pods/:alias
  "pods",
  "events",
  // sse/org/:orgAlias
  "org.stack_status_changed",
  "org.billing_updated",
  "vcs_connection.status_changed",
  "customer_identity.status_changed",
  // stream control (all endpoints)
  "connected",
  "heartbeat",
  "error",
] as const;

export type SseEventName = (typeof SSE_EVENT_NAMES)[number];

/** Control events handled by the hook; never forwarded as data messages. */
const CONTROL_EVENTS: ReadonlySet<string> = new Set([
  "connected",
  "heartbeat",
  "error",
]);

export interface UseSseResult<T = unknown> {
  /** Whether the EventSource is currently connected */
  connected: boolean;
  /** The last error from the EventSource (null when healthy) */
  error: string | null;
  /** Manually close and reopen the connection */
  reconnect: () => void;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useSse — generic Server-Sent Events hook using the native EventSource API.
 *
 * Connects to `path` (relative to apiBaseUrls.BACKEND) and invokes `onMessage`
 * for each incoming data event as an `SseMessage`. The JWT auth token is
 * appended as `?token=` query param because EventSource does not support
 * custom headers.
 *
 * Event delivery: the API's NestJS @Sse controllers emit **named** SSE events
 * (`event: deployment`, `event: deployment.log`, …). Named events never fire
 * the generic `message` listener, so this hook registers a listener per name
 * in `SSE_EVENT_NAMES` and adapts each payload into the `SseMessage` shape
 * (`{ type: eventName, data }`) consumed by the realtime hooks. Unnamed
 * legacy `message` events that embed `{ type, data }` in the payload are
 * still supported. `connected`, `heartbeat`, and `error` are control events
 * handled internally and never forwarded.
 *
 * Features:
 *   - Auto-reconnects (EventSource does this natively; we track state)
 *   - Cleans up listeners and the connection on unmount/reconnect
 *   - Stable callback via ref (no effect re-run on parent re-render)
 *   - Exposes `connected` and `error` state
 *
 * @param path       SSE endpoint path (e.g. "sse/stack/my-stack")
 * @param onMessage  Callback invoked for each SSE data message
 */
export function useSse<T = unknown>(
  path: string | null,
  onMessage: (msg: SseMessage<T>) => void,
): UseSseResult<T> {
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const callbackRef = useRef(onMessage);
  callbackRef.current = onMessage;

  const connect = useCallback(() => {
    if (!path) return;

    // Close existing connection
    eventSourceRef.current?.close();

    // EventSource sends the httpOnly session cookie automatically.
    // No token in the URL — cookies are sent with credentials by default.
    const base = apiBaseUrls.BACKEND;
    const url = `${base}/api/v1/${path}`;

    const es = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = es;

    es.onopen = () => {
      setConnected(true);
      setError(null);
    };

    es.onerror = () => {
      setConnected(false);
      setError("Connection lost — reconnecting…");
      // EventSource auto-reconnects; we just track state
    };

    // Shared dispatcher. Named events carry their type in the SSE `event:`
    // line and the data line is the payload itself; unnamed legacy messages
    // embed { type, data } in the JSON payload.
    const dispatch = (typeName: string, rawData: string) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(rawData);
      } catch {
        return; // Non-JSON data, skip
      }

      if (typeName === "message") {
        // Legacy unnamed envelope: { type, data } embedded in the payload.
        const envelope = parsed as Partial<SseMessage<T>>;
        const type = envelope?.type;
        if (type === undefined || CONTROL_EVENTS.has(type)) {
          if (type === "error") {
            const legacyMessage = envelope?.data as
              { message?: string } | undefined;
            setError(String(legacyMessage?.message ?? "SSE error"));
            es.close();
            setConnected(false);
          } else if (type === "connected") {
            setConnected(true);
            setError(null);
          }
          // heartbeat and unknown control names: ignore
          return;
        }
        callbackRef.current({ type, data: envelope.data as T });
        return;
      }

      // Named event: the data line is the payload itself.
      if (CONTROL_EVENTS.has(typeName)) {
        if (typeName === "error") {
          setError(
            String((parsed as { message?: string })?.message ?? "SSE error"),
          );
          es.close();
          setConnected(false);
        } else if (typeName === "connected") {
          setConnected(true);
          setError(null);
        }
        // heartbeat: ignore
        return;
      }
      callbackRef.current({ type: typeName, data: parsed as T });
    };

    // Named channels — required, because onmessage never fires for them.
    for (const name of SSE_EVENT_NAMES) {
      es.addEventListener(name, (event) =>
        dispatch(name, (event as MessageEvent).data as string),
      );
    }

    // Unnamed legacy envelope compatibility.
    es.onmessage = (event) => dispatch("message", event.data);
  }, [path]);

  useEffect(() => {
    connect();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connect]);

  const reconnect = useCallback(() => {
    connect();
  }, [connect]);

  return { connected, error, reconnect };
}
